/* eslint-disable react-compiler/react-compiler */
import { useRef } from 'react';

import { Fields } from '@/explore/types';
import { FieldOrGroup, isFieldGroup } from '@/explore/pipeline/utils';

export const createMissingFields = (fields: Fields, keys: string[]) => {
  return keys
    .filter((key) => !fields.some((field) => field.key === key))
    .map((key) => {
      let name = key;
      try {
        // TODO: figure out how to avoid json-key hack for when more info than field key is needed
        const parsed = JSON.parse(key);
        name = `${parsed.fieldKey ?? key}${'modelId' in parsed ? ` on model ${parsed.modelId}${'relationKey' in parsed ? `on relation ${parsed.relationKey}` : ''}` : ''}`;
      } catch {
        // Ignore
      }
      return {
        key,
        name: `❗ Field not found: ${name}`,
        type: 'Number' as const,
      };
    });
};

const fieldExists = <T extends Fields | FieldOrGroup[]>(key: string, fields: T): boolean =>
  fields.some(
    (field) => (isFieldGroup(field) && fieldExists(key, field.fields)) || field.key === key,
  );

/**
 * A hook to 'pad' the fields array with any missing fields and to explicitly
 * store them in react a ref so the missing fields stay present until the component
 * is destroyed to prevent fields disappearing immediately when they are made unnecessary.
 * Eg. when the missing field is deselected in an AddRelatedColumn operation form, the field should not
 * immediately pop out of existence, but also when the field re-appears as a valid entity, the final
 * filtering ensures we don't show a duplicate fake field.
 */
export const useEnsureFieldsExist = (fields: Fields, keys: string[]) => {
  const missingFieldsRef = useRef<Fields>([]);
  createMissingFields(fields, keys).forEach((f) => {
    if (!missingFieldsRef.current.some((field) => field.key === f.key)) {
      missingFieldsRef.current.push(f);
    }
  });
  return [
    ...missingFieldsRef.current.filter(({ key }) => !fieldExists(key ?? '', fields)),
    ...fields,
  ];
};

export const useEnsureFieldsExistGrouped = (fields: FieldOrGroup[], keys: string[]) => {
  const flattenedFields = fields.flatMap((field) => (isFieldGroup(field) ? field.fields : [field]));
  const missingFieldsRef = useRef<Fields>([]);
  createMissingFields(flattenedFields, keys).forEach((f) => {
    if (!missingFieldsRef.current.some((field) => field.key === f.key)) {
      missingFieldsRef.current.push(f);
    }
  });
  return [
    ...missingFieldsRef.current.filter(({ key }) => !fieldExists(key ?? '', fields)),
    ...fields,
  ];
};
